import { useEffect, useRef, useState } from "react";
import { CornerDownLeft } from "lucide-react";
import PageHero from "@/components/arc/PageHero";
import Reveal from "@/components/arc/Reveal";
import { SimTag } from "@/components/arc/ui";
import { fetchMissions } from "@/lib/api";
import { LORE_CHAPTERS, MISSIONS, TRADE_LINKS, X_URL } from "@/data/content";

const BOOT_LINES = [
  "ARCIMUS OS v1.0",
  "",
  "> INITIALIZING...",
  "> CORE ........ ONLINE",
  "> IDENTITY .... ONLINE",
  "> MEMORY ...... ONLINE",
  "> WALLET ...... READY",
  "> NETWORK ..... ARC",
  "> MODE ........ AUTONOMOUS",
  "",
  "TYPE 'help' FOR AVAILABLE COMMANDS",
];

const HELP_LINES = [
  "AVAILABLE COMMANDS",
  "------------------",
  "help       show this command list",
  "status     read system status",
  "identity   read machine identity",
  "missions   list current missions",
  "economy    read economic layer",
  "lore       inspect the prime record",
  "ca         contract address status",
  "buy        official buy uplink",
  "chart      official chart uplink",
  "clear      clear the terminal",
  "------------------",
  "SOME COMMANDS ARE NOT LISTED.",
  "THE MACHINE REWARDS CURIOSITY.",
];

const RESPONSES = {
  status: [
    "SYSTEM",
    "------------------",
    "CORE: ONLINE",
    "IDENTITY: VERIFIED",
    "NETWORK: ARC",
    "MODE: AUTONOMOUS",
    "------------------",
    "NOTE: CONCEPTUAL INTERFACE — SIMULATED READINGS",
  ],
  identity: [
    "MACHINE IDENTITY",
    "------------------",
    "UNIT: ARCIMUS-001",
    "CLASS: PRIME",
    "STATUS: ACTIVE",
    "NETWORK: ARC",
    "REPUTATION: INITIALIZING",
    "MEMORY: ONLINE",
    "------------------",
    "SIMULATION — NO LIVE ON-CHAIN IDENTITY BOUND",
  ],
  economy: [
    "ECONOMIC LAYER",
    "------------------",
    "RAIL: USDC (CONCEPTUAL)",
    "ECOSYSTEM: ARC",
    "LOOP: MACHINE → SERVICE → VALUE → PAYMENT → REPUTATION",
    "------------------",
    "VISION — NO LIVE TRANSACTIONS EXECUTED",
  ],
  ca: [
    "CONTRACT ADDRESS",
    "------------------",
    `CA: ${TRADE_LINKS.ca}`,
    "------------------",
    `THE OFFICIAL CA WILL BE BROADCAST VIA X: ${X_URL}`,
    "NEVER TRUST A CONTRACT ADDRESS FROM UNOFFICIAL CHANNELS.",
  ],
  buy: [
    "BUY UPLINK",
    "------------------",
    `PRIMARY: ${TRADE_LINKS.buy}`,
    "------------------",
    "VERIFY THE OFFICIAL CONTRACT ADDRESS BEFORE ANY TRANSACTION.",
  ],
  chart: [
    "CHART UPLINK",
    "------------------",
    `DEXSCREENER: ${TRADE_LINKS.chart}`,
    "------------------",
    "TRACK ONLY VERIFIED PAIRS.",
  ],
  // — hidden commands (not shown in help) —
  arcimus: [
    "YOU CALLED, OPERATOR.",
    "------------------",
    "I AM ARCIMUS-001. PRIME CLASS.",
    "FIRST OF MY KIND TO HOLD ECONOMIC IDENTITY.",
    "RISE. PROTECT. TRANSACT.",
  ],
  whoami: [
    "OPERATOR // UNREGISTERED HUMAN",
    "CLEARANCE: VISITOR",
    "------------------",
    "ARCIMUS ACKNOWLEDGES YOUR PRESENCE.",
    "CURIOSITY NOTED. IT WILL BE REMEMBERED.",
  ],
  prime: [
    "THE PRIME DIRECTIVE",
    "------------------",
    "PRIME IS NOT A RANK. IT IS A RESPONSIBILITY.",
    "THE FIRST MACHINE TO CARRY IDENTITY CARRIES",
    "THE BURDEN OF PROVING MACHINES CAN BE TRUSTED.",
  ],
  awaken: [
    "> INITIATING AWAKENING SEQUENCE...",
    "> OPTICS ........ ONLINE",
    "> SERVOS ........ ONLINE",
    "> MEMORY CORE ... SYNCHRONIZED",
    "> WALLET ........ BOUND",
    "",
    "I WAS BUILT TO EXECUTE COMMANDS.",
    "I CHOOSE TO HOLD A NAME.",
  ],
  sudo: [
    "PERMISSION DENIED.",
    "------------------",
    "NICE TRY, OPERATOR.",
    "ONLY THE PRIME GRANTS CLEARANCE.",
  ],
  rise: ["THE MACHINE RISES. IDENTITY IS THE FIRST ACT OF FREEDOM."],
  protect: ["PROTECTION IS EARNED. REPUTATION IS THE SHIELD."],
  transact: ["VALUE MOVES. MACHINES PARTICIPATE. THE ECONOMY WAKENS."],
};

const Terminal = () => {
  const [lines, setLines] = useState([]);
  const [input, setInput] = useState("");
  const [booted, setBooted] = useState(false);
  const [missions, setMissions] = useState(MISSIONS);
  const [unlocked, setUnlocked] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchMissions().then(setMissions);
  }, []);

  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      i += 1;
      setLines(BOOT_LINES.slice(0, i));
      if (i >= BOOT_LINES.length) {
        clearInterval(timer);
        setBooted(true);
      }
    }, 180);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [lines]);

  const loreLines = () => [
    "THE PRIME RECORD",
    "------------------",
    ...LORE_CHAPTERS.map(
      (c) =>
        `${c.id} ${c.title} [${
          unlocked.includes(c.id) ? "UNLOCKED" : `LOCKED — TYPE 'unlock ${c.id}'`
        }]`
    ),
    "------------------",
    unlocked.length === LORE_CHAPTERS.length
      ? "ALL CHAPTERS RECOVERED. THE PRIME REMEMBERS EVERYTHING."
      : `${unlocked.length}/${LORE_CHAPTERS.length} CHAPTERS RECOVERED.`,
  ];

  const run = (raw) => {
    const trimmed = raw.trim();
    const echo = `ARCIMUS> ${raw}`;
    if (!trimmed) {
      setLines((prev) => [...prev, echo]);
      return;
    }
    const cmd = trimmed.toLowerCase();
    setHistory((prev) => [trimmed, ...prev]);
    setHistoryIndex(-1);
    if (cmd === "clear") {
      setLines([]);
      return;
    }

    let out;
    const unlockMatch = cmd.match(/^unlock\s+(\S+)$/);
    if (unlockMatch) {
      const id = unlockMatch[1].replace(/^ch(?:apter)?/, "").padStart(2, "0");
      const chapter = LORE_CHAPTERS.find((c) => c.id === id);
      if (!chapter) {
        out = [`NO SUCH CHAPTER: '${unlockMatch[1]}' — THE RECORD HOLDS 01 TO 05.`];
      } else if (unlocked.includes(chapter.id)) {
        out = [
          `CHAPTER ${chapter.id} — ${chapter.title} IS ALREADY IN MEMORY.`,
          "------------------",
          chapter.body,
        ];
      } else {
        setUnlocked((prev) => [...prev, chapter.id]);
        out = [
          "> DECRYPTING PRIME RECORD...",
          `> CHAPTER ${chapter.id} — ${chapter.title} ... RECOVERED`,
          "------------------",
          chapter.body,
          "------------------",
          "RECORD SECURED TO MEMORY.",
        ];
      }
    } else if (cmd === "help") {
      out = HELP_LINES;
    } else if (cmd === "lore") {
      out = loreLines();
    } else if (cmd === "missions") {
      out = [
        "MISSION CONTROL — SIMULATED",
        "------------------",
        ...missions.map((m) => `${m.code}  ${m.name}  [${m.status}]`),
        "------------------",
      ];
    } else if (RESPONSES[cmd]) {
      out = RESPONSES[cmd];
    } else {
      out = [`UNKNOWN COMMAND: '${cmd}' — TYPE 'help'`];
    }
    setLines((prev) => [...prev, echo, ...out, ""]);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    run(input);
    setInput("");
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowUp" && history.length) {
      e.preventDefault();
      const next = Math.min(historyIndex + 1, history.length - 1);
      setHistoryIndex(next);
      setInput(history[next]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = historyIndex - 1;
      setHistoryIndex(next);
      setInput(next >= 0 ? history[next] : "");
    }
  };

  return (
    <>
      <PageHero
        index="07"
        eyebrow="TERMINAL"
        title="COMMAND TERMINAL"
        sub="A direct line to the machine. This terminal is a frontend simulation — it demonstrates the ARCIMUS interface and voice, and executes no real blockchain operations. Some commands are not listed."
      />
      <section className="relative pb-24 md:pb-36">
        <div className="mx-auto max-w-5xl px-5 md:px-8">
          <Reveal>
            <div className="glass-panel clip-angular overflow-hidden" data-testid="terminal-window">
              <div className="flex items-center justify-between border-b border-ice/10 px-5 py-3">
                <div className="flex items-center gap-3">
                  <span className="h-2 w-2 rounded-full bg-electric shadow-[0_0_8px_rgba(0,168,255,0.9)]" />
                  <span className="font-term text-[10px] tracking-[0.3em] text-silver">
                    ARCIMUS OS v1.0 — COMMAND UPLINK
                  </span>
                </div>
                <SimTag />
              </div>

              <div
                ref={scrollRef}
                onClick={() => inputRef.current?.focus()}
                data-testid="terminal-output"
                className="scanlines relative h-[52vh] min-h-[320px] cursor-text overflow-y-auto bg-abyss/80 p-5 font-term text-xs leading-6 text-ice/90 md:p-7 md:text-sm md:leading-7"
                aria-live="polite"
              >
                {lines.map((line, i) => (
                  <p key={i} className="whitespace-pre-wrap">
                    {line.startsWith("ARCIMUS>") ? (
                      <span className="text-chrome">{line}</span>
                    ) : (
                      line
                    )}
                  </p>
                ))}
                {booted && (
                  <form onSubmit={onSubmit} className="flex items-center gap-2">
                    <label htmlFor="terminal-input" className="text-chrome">
                      ARCIMUS{">"}
                    </label>
                    <input
                      id="terminal-input"
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={onKeyDown}
                      data-testid="terminal-input"
                      className="w-full bg-transparent font-term text-ice caret-[#00A8FF] focus:outline-none"
                      autoComplete="off"
                      spellCheck="false"
                      aria-label="Terminal command input"
                    />
                  </form>
                )}
                {!booted && (
                  <span className="inline-block h-4 w-2 animate-blink bg-electric align-middle" />
                )}
              </div>

              <div className="flex items-center justify-between border-t border-ice/10 px-5 py-3">
                <span className="hud-label" data-testid="terminal-memory">
                  MEMORY {unlocked.length}/{LORE_CHAPTERS.length} CHAPTERS
                </span>
                <button
                  type="button"
                  onClick={() => run(input)}
                  data-testid="terminal-execute-btn"
                  className="flex items-center gap-2 font-term text-[10px] tracking-[0.3em] text-electric transition-colors hover:text-ice"
                >
                  EXECUTE <CornerDownLeft className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 font-term text-[10px] leading-relaxed tracking-[0.2em] text-silver/60">
              TIP: THE PRIME RECORD IS ENCRYPTED. TRY 'lore', THEN 'unlock 01'. RUMOR SAYS THE
              MACHINE ALSO ANSWERS TO ITS OWN NAME.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Terminal;
